import React, { FC, ReactNode } from 'react';
import { Box, Container, Divider, Grid, makeStyles, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { ReadButton } from 'components/atoms';
import { PinIcon } from 'svgs/projects';
import clsx from 'clsx';
import { ProjectModalContentProps } from './ProjectModalContent';
import { truncateString } from 'blog/utils/strings';

export interface TabPanelProps {
  address: string;
  title: string;
  description: string;
  href: string;
  image: string;
  indexTabPanel: any;
  value?: any;
  addressIcon: ReactNode;
  addressName?: string;
  addressIconMobile?: ReactNode;
  reverse?: boolean;
  onReadMore?: (item: ProjectModalContentProps) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  address: {
    fontWeight: 500,
    paddingLeft: theme.spacing(4.5),
  },
  textBlock: {
    border: `1px solid #E1DEDD`,
    padding: theme.spacing(4),
    maxHeight: '100%',
    minHeight: 400,
    position: 'relative',
    order: 2,
    [theme.breakpoints.up('md')]: {
      order: 1,
    },
    [theme.breakpoints.up('lg')]: {
      maxHeight: 400,
    },
  },
  icon: {
    position: 'absolute',
    top: theme.spacing(4.3),
    left: theme.spacing(4),
  },
  title: {
    fontWeight: 500,
    color: theme.palette.primary.dark,
    marginBottom: theme.spacing(3),
  },
  description: {
    lineHeight: 1.8,
    paddingBottom: theme.spacing(3),
  },
  imageBlock: {
    position: 'relative',
    order: 1,
    [theme.breakpoints.up('md')]: {
      order: 2,
    },
  },
  imageWrapper: {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '100%',
    height: '100%',
    minHeight: 215,
  },
  textIcon: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: theme.palette.primary.main,
    maxWidth: 80,
    minWidth: 80,
    maxHeight: 80,
    minHeight: 80,
  },
  textAddress: {
    color: theme.palette.common.white,
    fontWeight: 500,
    paddingLeft: theme.spacing(3),
    fontSize: 13,
    [theme.breakpoints.up('sm')]: {
      fontSize: 20,
      lineHeight: '36px',
    },
  },
  divider: {
    maxWidth: 420,
    backgroundColor: theme.palette.secondary.light,
  },
}));

function TabPanel(props: TabPanelProps) {
  const {
    address,
    href,
    image,
    description,
    title,
    value,
    indexTabPanel,
    addressIcon,
    addressName,
    addressIconMobile,
    reverse,
    onReadMore,
    ...other
  } = props;
  const classes = useStyles();
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  return (
    <div
      role="tabpanel"
      hidden={value !== indexTabPanel}
      id={`vertical-tabpanel-${indexTabPanel}`}
      aria-labelledby={`vertical-tab-${indexTabPanel}`}
      {...other}
    >
      {value === indexTabPanel && (
        <Grid container direction={reverse ? 'row-reverse' : 'row'} justifyContent="space-between">
          <Grid xs={12} md={6} className={classes.textBlock} item>
            <Grid container direction="column" style={{ minHeight: '100%' }} justifyContent="space-between">
              <Grid item>
                {address && (
                  <>
                    <PinIcon className={classes.icon} />
                    <Typography variant="body1" color="textSecondary" className={classes.address}>
                      {address}
                    </Typography>
                    <Box mt={3} mb={4}>
                      <Divider className={classes.divider} />
                    </Box>
                  </>
                )}
                <Typography variant="h5" className={classes.title}>
                  {title}
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.description}
                  dangerouslySetInnerHTML={{
                    __html: truncateString(description, 340),
                  }}
                />
              </Grid>
              <Grid item>
                <ReadButton
                  text="Read more"
                  onClick={() =>
                    onReadMore({
                      description,
                      address,
                      image,
                      title,
                    })
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid xs={12} md={6} className={clsx(classes.imageBlock)} item>
            <Box
              display="flex"
              position="row"
              justifyContent="center"
              alignItems={'center'}
              zIndex={1}
              className={classes.textIcon}
            >
              <Box>{addressIcon}</Box>
              {!!addressName && <Typography className={classes.textAddress}>{addressName}</Typography>}
            </Box>
            <Box className={classes.imageWrapper} style={{ backgroundImage: `url("${image}")` }} />
          </Grid>
        </Grid>
      )}
    </div>
  );
}

const ContentSlider: FC<TabPanelProps> = (props) => {
  const {
    address,
    title,
    description,
    href,
    image,
    indexTabPanel,
    value,
    addressIcon,
    addressName,
    addressIconMobile,
    reverse,
    onReadMore,
  } = props;

  return (
    <Container fixed>
      <TabPanel
        value={value}
        indexTabPanel={indexTabPanel}
        address={address}
        href={href}
        image={image}
        description={description}
        title={title}
        addressIcon={addressIcon}
        addressName={addressName}
        addressIconMobile={addressIconMobile}
        reverse={reverse}
        onReadMore={onReadMore}
      />
      <Box mt={6} />
    </Container>
  );
};

export default ContentSlider;
